import { CalendarDate, DateValue } from '@internationalized/date';
import { Spacing, Typography } from '@persona-id/design-tokens';
import { CircleInfoIcon } from '@persona-id/design-tokens/icons';
import { RangeValue } from '@react-types/shared';
import { isEmpty } from 'lodash/fp';
import { DateTime } from 'luxon';
import { FC } from 'react';
import styled from 'styled-components';

import EventNameSticker from 'dashboard/components/EventNameSticker';
import HoverCopyableField from 'dashboard/components/HoverCopyableField';
import Pagination from 'dashboard/components/Pagination';
import { MonoSpan } from 'dashboard/components/Typography';
import WebhookEventStatusSticker from 'dashboard/components/WebhookEventStatusSticker';
import { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import TableControls, { FilterConfig } from 'dashboard/components-lib/TableControls';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import TableLayout, {
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import { UseTableViewsState } from 'dashboard/lib/hooks/useTableViews';
import { titleCase } from 'dashboard/lib/utils/stringFormat';
import { WebhookEventsAttributeFilters } from 'dashboard/models/table-view';
import { WebhookEvent, WebhookEventStatus } from 'dashboard/models/webhook-event';
import { WebhookEventsFilterParams } from 'dashboard/redux/slices/webhook-events';
import Banner from 'design-system/components/Banner';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { DATE_AND_TIME_MILLIS_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import { PaginationParams } from 'lib/interfaces/api';

interface DashboardWebhookEventsViewProps {
  webhookEvents: WebhookEvent[];
  isLoading: boolean;
  pageSize: number;
  paginationParams: PaginationParams;
  handlePageSizeClick: (pageSize: number) => void;
  handlePageLinkClick: (pageNumer: number) => void;
  filters?: WebhookEventsFilterParams;
  handleDateUpdate: (range: RangeValue<DateValue> | null) => void;
  handleOmniSearch: (omniQuery: string) => void;
  handleStatusFilterUpdate: (status: WebhookEventStatus[]) => void;
  tableViewsState: UseTableViewsState<WebhookEventsAttributeFilters>;
}

const STATUS_FILTER_OPTIONS = Object.values(WebhookEventStatus).map((status) => ({
  label: titleCase(status),
  value: status,
}));

const DashboardWebhookEventsView: FC<DashboardWebhookEventsViewProps> = ({
  webhookEvents,
  isLoading,
  pageSize,
  paginationParams,
  handlePageSizeClick,
  handlePageLinkClick,
  filters = {
    eventId: undefined,
    webhookId: undefined,
    status: undefined,
    updatedAtStart: undefined,
    updatedAtEnd: undefined,
    omni: undefined,
  },
  handleDateUpdate,
  handleOmniSearch,
  handleStatusFilterUpdate,
  tableViewsState,
}) => {
  const { status, updatedAtStart, updatedAtEnd, omni } = filters;
  const hasUsedFilters = !Object.values(filters).every(isEmpty);

  const statusFilterLabelTextMapper = useLabelTextMapper(STATUS_FILTER_OPTIONS, 'Statuses', {
    emptyIsAll: true,
  });

  const ninetyDaysAgo = DateTime.local().minus({ days: 90 });

  const filterConfig: FilterConfig[] = [
    {
      id: 'status',
      name: 'Status',
      type: 'multiselect-legacy',
      selectOptionValues: STATUS_FILTER_OPTIONS,
      labelTextMapper: statusFilterLabelTextMapper,
      onChange: handleStatusFilterUpdate,
      value: status || [],
    },
    {
      id: 'updatedAt',
      name: `Updated at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`,
      type: 'daterange',
      onChange: handleDateUpdate,
      value: {
        startDate: updatedAtStart,
        endDate: updatedAtEnd,
      },
      datePickerProps: {
        minDate: new CalendarDate(ninetyDaysAgo.year, ninetyDaysAgo.month, ninetyDaysAgo.day),
      },
    },
  ];

  return (
    <>
      <Container>
        <Header title='Webhook Events' />
        <Section>
          <Card>
            <Card.Body>
              <Card.Title>Webhook Events</Card.Title>
              <StyledBanner variant='warning'>
                <CircleInfoIcon />
                Webhook events are subject to a 90 day retention policy. They are deleted 90 days
                after their creation and then are inaccessible via the Dashboard or API.
              </StyledBanner>
              <Spacer height={1} />
              <TableControls
                showViews={tableViewsState.canViewTableViews}
                canEditViews={tableViewsState.canWriteTableViews}
                views={tableViewsState.tableViews.map((v) => ({
                  id: v.id,
                  name: v.attributes.name,
                }))}
                currentView={tableViewsState.currentTableView?.id}
                onSelectView={tableViewsState.handleSetCurrentTableView}
                onAddView={async () => {
                  return tableViewsState.handleCreateTableView('New View', {
                    columns: [],
                    filters,
                  });
                }}
                onViewNameChange={tableViewsState.handleSaveTableView}
                onDeleteView={tableViewsState.handleArchiveTableView}
                onDuplicateView={tableViewsState.handleDuplicateTableView}
                onSaveView={async () => {
                  if (tableViewsState.currentTableView) {
                    return tableViewsState.handleSaveTableView(
                      tableViewsState.currentTableView.id,
                      tableViewsState.currentTableView.attributes.name,
                      { columns: [], filters },
                    );
                  }
                }}
                isViewDirty={tableViewsState.isDirty}
                searchProps={{
                  id: 'webhook-events-table__search',
                  handleInput: handleOmniSearch,
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  value: omni,
                  placeholder: 'Webhook ID, Webhook Event ID, Event ID, or Object ID',
                }}
                filters={filterConfig}
              />
              <TableLayout
                dataTest='webhook-events-view__webhook-events-table'
                isLoading={isLoading}
                isEmpty={webhookEvents.length === 0}
                showHeader={hasUsedFilters}
                headerTR={
                  <>
                    <TableTH>Webhook Event ID</TableTH>
                    <TableTH>Event Name</TableTH>
                    <TableTH>Related Event ID</TableTH>
                    <TableTH>Status (Code)</TableTH>
                    <TableTH>Attempts Left</TableTH>
                    <TableTH>{`Updated at (${DateTime.local().toFormat(
                      TIMEZONE_NAME_ABBREVIATED,
                    )})`}</TableTH>
                  </>
                }
                nullState={
                  <TableNullState
                    dataTestBlock='webhook-events-view'
                    hasUsedFilters={hasUsedFilters}
                  >
                    No Webhook Events have been created yet.
                  </TableNullState>
                }
              >
                {webhookEvents.map((webhookEvent) => (
                  <TableLinkTR
                    to={`/dashboard/webhook-events/${webhookEvent.id}`}
                    state={{ internalRedirect: true }}
                    key={webhookEvent.id}
                    id='webhook-event-table__row'
                  >
                    <TableTD>
                      <HoverCopyableField
                        copyValue={webhookEvent.id}
                        confirmationText='Copied webhook ID'
                      >
                        <MonoSpan>{webhookEvent.id}</MonoSpan>
                      </HoverCopyableField>
                    </TableTD>
                    <TableTD>
                      <EventNameSticker eventName={webhookEvent.attributes['event-name']} />
                    </TableTD>
                    <TableTD>
                      <HoverCopyableField
                        copyValue={webhookEvent.relationships?.['event']?.data?.id}
                        confirmationText='Copied related event ID'
                      >
                        <MonoSpan>{webhookEvent.relationships?.['event']?.data?.id}</MonoSpan>
                      </HoverCopyableField>
                    </TableTD>
                    <TableTD>
                      <WebhookEventStatusSticker webhookEvent={webhookEvent} />
                    </TableTD>
                    <TableTD>
                      <MonoSpan>{webhookEvent.attributes['attempts-left']}</MonoSpan>
                    </TableTD>
                    <TableTD>
                      {DateTime.fromISO(webhookEvent.attributes['updated-at']).toFormat(
                        DATE_AND_TIME_MILLIS_FORMAT,
                      )}
                    </TableTD>
                  </TableLinkTR>
                ))}
              </TableLayout>
              <Pagination
                pageSize={pageSize}
                paginationParams={paginationParams}
                handlePageSizeClick={handlePageSizeClick}
                handlePageLinkClick={handlePageLinkClick}
              />
            </Card.Body>
          </Card>
        </Section>
      </Container>
    </>
  );
};

const StyledBanner = styled(Banner)`
  ${Typography.TextMedium};
  padding: ${Spacing[4]};
`;

export default DashboardWebhookEventsView;
